// Agency / team plan tiers. Mirrors what the create-checkout backend
// resolves to Stripe price IDs. Update both this file and the server's
// `priceFor()` together when adding tiers.
//
// Pricing model: a single paid 'agency' tier that includes 3 seats
// (1 broker + 2 agents). Additional seats are billed as a per-seat
// add-on (`SEAT_ADDON_TIERS`) on a second Stripe line item — the agency
// owner adds/removes seats from the Team page after activation. The
// per-seat price uses graduated/marginal tier pricing (see below).

export const AGENCY_BASE_SEATS = 3;

// Per-seat add-on prices use *graduated* (marginal) tier pricing — the
// first 9 extra seats are billed at the tier-1 rate, the next 10 at
// tier-2, and so on. Mirrors how Stripe's `tiers_mode: graduated`
// price object should be configured for STRIPE_AGENCY_SEAT_PRICE_ID
// and STRIPE_AGENCY_SEAT_YEARLY_PRICE_ID. `up_to` is the inclusive
// upper bound of the bracket; `null` means "and above" (matches
// Stripe's `up_to: "inf"`).
export const SEAT_ADDON_TIERS = [
  { up_to: 9,    monthly_aed: 99, yearly_aed: 999, label: '1–9 seats' },
  { up_to: 19,   monthly_aed: 79, yearly_aed: 799, label: '10–19 seats' },
  { up_to: 29,   monthly_aed: 59, yearly_aed: 599, label: '20–29 seats' },
  { up_to: null, monthly_aed: 29, yearly_aed: 299, label: '30+ seats' },
];

// Walks `SEAT_ADDON_TIERS` and returns a per-tier breakdown for `qty`
// extra seats at the given billing cycle. Each line carries the seat
// range (from/to), per-seat rate, and subtotal. Used both to render the
// live cost breakdown in TeamPage and to compute totals.
export function seatAddonBreakdown(qty, billing) {
  const rateKey = billing === 'yearly' ? 'yearly_aed' : 'monthly_aed';
  const lines = [];
  let consumed = 0;
  let remaining = Math.max(0, qty);
  for (const tier of SEAT_ADDON_TIERS) {
    if (remaining <= 0) break;
    const tierCap = tier.up_to === null ? Infinity : tier.up_to;
    const tierSize = tierCap - consumed;
    const take = Math.min(remaining, tierSize);
    lines.push({
      from: consumed + 1,
      to: consumed + take,
      seats: take,
      rate: tier[rateKey],
      subtotal: take * tier[rateKey],
    });
    consumed += take;
    remaining -= take;
  }
  return lines;
}

export function seatAddonCost(qty, billing) {
  return seatAddonBreakdown(qty, billing).reduce((s, l) => s + l.subtotal, 0);
}

// The marginal per-seat rate for the *next* seat at the given current
// quantity. Useful for "next seat costs X" microcopy.
export function nextSeatRate(qty, billing) {
  const rateKey = billing === 'yearly' ? 'yearly_aed' : 'monthly_aed';
  for (const tier of SEAT_ADDON_TIERS) {
    const tierCap = tier.up_to === null ? Infinity : tier.up_to;
    if (qty < tierCap) return tier[rateKey];
  }
  return SEAT_ADDON_TIERS[SEAT_ADDON_TIERS.length - 1][rateKey];
}

export const AGENCY_PLANS = {
  // 'agency-free' is the initial placeholder plan an org is given when
  // created. It does NOT appear in the plan picker — the AgencyApp gates
  // access behind a paid plan, so this label only surfaces in admin views
  // and the "subscribe to unlock" paywall.
  'agency-free': {
    id: 'agency-free',
    name: 'Setup pending',
    blurb: 'Choose a plan below to activate your agency workspace.',
    seat_limit: 2,
    monthly_aed: 0,
    yearly_aed: 0,
    cta: 'Choose a plan',
    accent: '#64748B',
  },
  agency: {
    id: 'agency',
    name: 'Agency',
    blurb: 'Full Pro/Agency access for one broker and two agents under your agency. Add more seats any time — AED 99/mo each, with volume discounts at 10+, 20+ and 30+ seats.',
    seat_limit: AGENCY_BASE_SEATS,
    monthly_aed: 499,
    yearly_aed: 4999,
    cta: 'Activate Agency',
    accent: '#38BDF8',
    popular: true,
  },
};

// Plan picker order — agency-free is excluded since it's not a real
// choice the user can make. It's only an initial placeholder state.
export const AGENCY_TIER_ORDER = ['agency'];

export function getAgencyPlan(planId) {
  return AGENCY_PLANS[planId] || AGENCY_PLANS['agency-free'];
}

// True only when the org is on a paid agency tier and currently active.
// Past-due / canceled / free orgs all return false. AgencyApp uses this
// to gate access behind the paywall.
export function isAgencyPaid(org) {
  if (!org) return false;
  return org.plan === 'agency' && org.status === 'active';
}
